// City-specific content for SEO location pages.
// Each city has unique intro copy, neighborhood mentions and FAQ so
// pages are original (no doorway-page duplication). Content is honest —
// no invented projects or claims about specific installs per city.

export const CITIES = {
  tampa: {
    slug: "tampa",
    name: "Tampa",
    state: "FL",
    primary_keyword: "custom media walls Tampa",
    supporting: ["TV wall installation Tampa", "accent walls Tampa", "fireplace TV walls Tampa"],
    seo_title: "Custom Media Walls Tampa | Swift Luxury Walls",
    meta_description:
      "Transform your Tampa home with a custom media wall, electric fireplace, wood slat accents and built-in storage. Start designing with Swift Luxury Walls.",
    hero_heading: "Custom Media Walls & Accent Walls in Tampa, FL",
    hero_sub:
      "From Hyde Park bungalows to Water Street high-rises, we build entertainment features that fit the way you actually live — engineered around your TV, your room, and your day-to-day.",
    neighborhoods: [
      "Hyde Park",
      "South Tampa",
      "Westshore",
      "Downtown & Water Street",
      "Channelside",
      "SoHo",
      "Bayshore Beautiful",
      "Davis Islands",
      "New Tampa",
    ],
    focus:
      "Tampa homes range from historic wood-floor bungalows to new-construction condos — each needs a different approach to feature-wall building. We handle the design, materials, cabling and finish so the wall becomes the room's anchor.",
    services_intro:
      "Custom media walls, floating consoles, electric-fireplace features, wood-slat and acoustic panel accents, marble-look feature walls, integrated lighting and custom cabinetry — designed and installed locally in the Tampa area.",
    local_details:
      "We coordinate with Tampa condo boards and HOAs when the project is in a mid-rise or high-rise building, and our shop is a short drive from most Tampa neighborhoods — meaning fewer travel-day surcharges and quicker punch-list visits than out-of-town competitors.",
    faqs: [
      {
        q: "Do you work in Tampa condos and high-rises?",
        a: "Yes. We're used to service-elevator scheduling, condo insurance requirements, and quiet-hours rules that come with buildings in Downtown, Water Street and Channelside. We'll coordinate directly with your association when needed.",
      },
      {
        q: "How long does a typical Tampa media wall take?",
        a: "Most single-wall projects install in one to three days on-site once materials are in. Design and material lead time is typically two to four weeks — a bit longer for imported stone slabs.",
      },
      {
        q: "Can you work with a TV I already own?",
        a: "Absolutely. We size the recess or mount to your exact TV, plan the outlet and cable path, and can integrate your soundbar, speakers and receivers cleanly into the build.",
      },
      {
        q: "Do you build accent walls without a TV?",
        a: "Yes — slat walls, PU stone veneer features, painted paneling and marble-look accents in living rooms, dining rooms, bedrooms and offices are a significant part of what we build in Tampa.",
      },
    ],
  },

  brandon: {
    slug: "brandon",
    name: "Brandon",
    state: "FL",
    primary_keyword: "custom media walls Brandon FL",
    supporting: [
      "entertainment walls Brandon FL",
      "TV feature walls Brandon",
      "accent wall installation Brandon",
    ],
    seo_title: "Custom Media Walls Brandon FL | Swift Luxury Walls",
    meta_description:
      "Custom entertainment walls, feature installations and built-in storage for Brandon homes. Design your wall online with Swift Luxury Walls.",
    hero_heading: "Custom Media Walls & Entertainment Walls in Brandon, FL",
    hero_sub:
      "Brandon families want a great room that feels finished — not just a TV on drywall. We design and build media walls, slat accents and floating consoles that turn everyday rooms into a real focal point.",
    neighborhoods: ["Bloomingdale", "Buckhorn", "Providence Lakes", "Sterling Ranch", "Lakewood Ridge"],
    focus:
      "Brandon's mix of established single-family homes and newer subdivisions means we spend time up-front planning around existing outlets, load-bearing walls and open-concept great rooms. Every build is customized — no off-the-shelf modules.",
    services_intro:
      "Custom entertainment walls, TV feature walls, slat accent walls, electric-fireplace media walls, custom cabinetry and shelving — installed for homeowners across Brandon.",
    local_details:
      "Brandon is a short drive from our shop, so we can typically schedule site visits within a few days. Most Brandon projects are single-family homes, which means faster access, wider working room and simpler logistics than condo builds.",
    faqs: [
      {
        q: "Do you serve Brandon and the surrounding areas?",
        a: "Yes — Brandon, Bloomingdale, Valrico and Seffner are all in our regular service zone. No travel surcharges within this area.",
      },
      {
        q: "Can you match an existing paint color or finish in my Brandon home?",
        a: "Yes. We match cabinet and paint colors to what you already have, and can bring physical samples on the consult so you can see them in your actual light.",
      },
      {
        q: "How much does a media wall in Brandon typically cost?",
        a: "Cost depends on materials, size, fireplace and cabinetry. Our wall builder collects the details we need to give you an honest range — most Brandon single-wall projects fall between $3,500 and $12,000.",
      },
      {
        q: "Can you build around existing built-ins or cabinetry?",
        a: "Often, yes — we'll assess on the consult whether the existing pieces can be integrated cleanly or if it's better to remove and rebuild.",
      },
    ],
  },

  riverview: {
    slug: "riverview",
    name: "Riverview",
    state: "FL",
    primary_keyword: "custom media walls Riverview FL",
    supporting: [
      "TV wall installation Riverview",
      "fireplace media walls Riverview",
      "custom entertainment centers Riverview",
    ],
    seo_title: "Custom Media Walls Riverview FL | Swift Luxury Walls",
    meta_description:
      "Design a custom media wall or entertainment center for your Riverview home. TV walls, fireplaces, slat accents — locally designed and installed.",
    hero_heading: "Custom Media Walls & Entertainment Centers in Riverview, FL",
    hero_sub:
      "Riverview's newer builds have great bones — high ceilings, open floor plans, big blank walls — and that's exactly where a feature-wall install goes from nice to unforgettable.",
    neighborhoods: ["Bell Creek", "Panther Trace", "Boyette Farms", "South Fork", "Rivercrest", "Summerfield"],
    focus:
      "A lot of Riverview's housing stock was built in the last 10-20 years — meaning modern layouts with plenty of vertical space to work with. We take advantage of that with taller feature walls, integrated fireplaces and floor-to-ceiling slats.",
    services_intro:
      "Custom media walls, TV wall installations, electric-fireplace media walls, floating consoles, slat and acoustic panel accents and full entertainment centers for Riverview homes.",
    local_details:
      "Riverview is close to our shop, so scheduling and follow-up visits are simple. We're familiar with the common floor plans in Bell Creek, Panther Trace and South Fork — helpful for planning around outlets and structural walls.",
    faqs: [
      {
        q: "My great room is very tall — can you build up to the ceiling?",
        a: "Yes. High-ceiling great rooms are one of our favorite Riverview builds — floor-to-ceiling feature walls with layered materials and integrated lighting make the space feel finished.",
      },
      {
        q: "Do you install electric fireplaces?",
        a: "We do — recessed and slim-line linear electric fireplaces built directly into the wall, sized to your TV and layout. We handle the electrical planning and finish surround.",
      },
      {
        q: "Can I bring my own materials or fixtures?",
        a: "You can — but we'll walk you through it. Some off-the-shelf products aren't rated for enclosed builds. We'd rather flag it up front than surprise you at install.",
      },
      {
        q: "How soon can you start a Riverview project?",
        a: "Most Riverview projects move from consult to install within four to eight weeks depending on material lead times.",
      },
    ],
  },

  "land-o-lakes": {
    slug: "land-o-lakes",
    name: "Land O' Lakes",
    state: "FL",
    primary_keyword: "custom media walls Land O' Lakes FL",
    supporting: [
      "entertainment walls Land O' Lakes",
      "wood slat walls Land O' Lakes",
      "TV wall installation Land O' Lakes",
    ],
    seo_title: "Custom Media Walls Land O' Lakes FL | Swift Luxury Walls",
    meta_description:
      "Custom entertainment walls, wood slat features and TV wall installation for Land O' Lakes homes. Design your project with Swift Luxury Walls.",
    hero_heading: "Custom Media Walls & Slat Wall Installation in Land O' Lakes, FL",
    hero_sub:
      "Land O' Lakes homes often have that unfinished great-room wall that begs for something more than paint. We turn it into a feature — media wall, slat panel, marble accent or full built-in.",
    neighborhoods: ["Connerton", "Bexley", "Lake Padgett", "Lakeshore Ranch", "Ballantrae", "Wilderness Lake Preserve"],
    focus:
      "Land O' Lakes has grown fast with high-ceiling, open-plan homes that leave a lot of blank wall real estate. A well-built feature wall gives that room a purpose — and gives your TV a home that isn't just a bracket.",
    services_intro:
      "Custom entertainment walls, wood-slat and acoustic panel installations, TV wall builds, electric-fireplace features and floating consoles for Land O' Lakes homeowners.",
    local_details:
      "Land O' Lakes is a moderate drive from our shop. We batch consults and installs efficiently in this area — planning site visits so you get thoughtful attention without lots of small trip charges added to your invoice.",
    faqs: [
      {
        q: "Are wood slat walls popular in Land O' Lakes?",
        a: "Yes — the newer homes in Connerton, Bexley and Lakeshore Ranch tend to have tall living areas that look outstanding with a full-height slat feature wall behind the TV or as an accent.",
      },
      {
        q: "Can you install slats over existing paint?",
        a: "In most cases yes, provided the wall is flat and structurally sound. We assess on the consult before quoting.",
      },
      {
        q: "How is a slat wall attached — will it damage my drywall?",
        a: "Slat panels are typically mounted with a hidden furring system. Removal is possible; we'll walk through what remediation would look like if you ever want to change it out.",
      },
      {
        q: "Do you build in Wesley Chapel and nearby?",
        a: "Yes — Wesley Chapel, Lutz and Odessa are close neighbors and part of our regular service area.",
      },
    ],
  },

  clearwater: {
    slug: "clearwater",
    name: "Clearwater",
    state: "FL",
    primary_keyword: "custom media walls Clearwater FL",
    supporting: [
      "accent wall installation Clearwater",
      "fireplace TV walls Clearwater",
      "custom entertainment centers Clearwater",
    ],
    seo_title: "Custom Media Walls Clearwater FL | Swift Luxury Walls",
    meta_description:
      "Coastal-friendly media walls, accent installations and custom entertainment centers for Clearwater and Clearwater Beach homes.",
    hero_heading: "Custom Media Walls & Accent Walls in Clearwater, FL",
    hero_sub:
      "Coastal condos, mid-century single-family homes and new-build showstoppers — Clearwater has all three, and we build feature walls that respect the light, the salt air and the neighbors on the other side.",
    neighborhoods: [
      "Sand Key",
      "Clearwater Beach",
      "Belleair",
      "Countryside",
      "Downtown Clearwater",
      "Feather Sound",
    ],
    focus:
      "A Clearwater feature wall has to hold up to strong afternoon light and coastal humidity — that changes what we recommend for finishes. Panels, veneers and hardware are all chosen with that in mind so the build looks as good in year five as in year one.",
    services_intro:
      "Custom media walls, accent-wall installation, electric-fireplace TV walls, custom cabinetry and built-in storage — designed and installed across Clearwater, Clearwater Beach and Belleair.",
    local_details:
      "Beach-side and coastal condos have their own set of build rules: elevator scheduling, protective floor coverings and quiet-hours compliance. We handle association coordination for Clearwater properties and pick finishes that hold up in humid, salt-air environments.",
    faqs: [
      {
        q: "Do you install in Clearwater Beach condos?",
        a: "Yes — including Sand Key and Belleair Beach buildings. We handle association coordination and use protective coverings on shared corridors and elevators.",
      },
      {
        q: "Can you use materials that handle coastal humidity?",
        a: "Yes. We steer toward panel systems, sealed veneers and hardware that hold up well to humidity — some solid-wood options aren't ideal directly on an exterior-facing wall in a coastal condo.",
      },
      {
        q: "Do afternoon glare or sun exposure factor into design?",
        a: "It should. High-gloss finishes can be beautiful in the right room and washed-out in a Clearwater west-facing living room. We'll flag those trade-offs on the consult.",
      },
      {
        q: "Is Belleair, Largo and Dunedin part of your service area?",
        a: "Yes — all Pinellas County towns near Clearwater are part of our regular routes.",
      },
    ],
  },

  "st-petersburg": {
    slug: "st-petersburg",
    name: "St. Petersburg",
    state: "FL",
    primary_keyword: "custom media walls St. Petersburg FL",
    supporting: [
      "TV wall installation St. Petersburg",
      "accent walls St. Pete",
      "entertainment walls St. Petersburg",
    ],
    seo_title: "Custom Media Walls St. Petersburg FL | Swift Luxury Walls",
    meta_description:
      "Custom TV walls, accent installations and built-in entertainment centers for St. Pete homes — from historic bungalows to downtown condos.",
    hero_heading: "Custom Media Walls & TV Wall Installation in St. Petersburg, FL",
    hero_sub:
      "St. Pete's charm is the mix — a 1920s Craftsman in Old Northeast, a mid-century in Coquina Key, a brand-new tower unit downtown. Every one deserves a wall that reflects what makes it special.",
    neighborhoods: [
      "Old Northeast",
      "Downtown St. Pete",
      "Snell Isle",
      "Historic Kenwood",
      "Coquina Key",
      "Pinellas Point",
      "Shore Acres",
    ],
    focus:
      "Historic St. Pete homes often have plaster walls, older wiring and character trim we want to preserve. Downtown condos have concrete walls and shared-service considerations. We approach each build with the home's era and construction in mind.",
    services_intro:
      "Custom media walls, TV wall installations, accent walls (slat, marble-look, painted panels), floating consoles and custom shelving for St. Petersburg homeowners.",
    local_details:
      "We're familiar with older-home construction quirks in Old Northeast and Historic Kenwood, and we handle association access for downtown and Snell Isle condos. Downtown parking and elevator windows are planned in advance so install days run smoothly.",
    faqs: [
      {
        q: "Do you work in older St. Pete homes with plaster walls?",
        a: "Yes. Plaster requires a different mounting approach than modern drywall; we bring the right anchors and take the time to protect the original wall while we build.",
      },
      {
        q: "Can you build in downtown St. Pete condos?",
        a: "Yes — we coordinate elevator time, floor protection and association insurance requirements for downtown high-rises.",
      },
      {
        q: "Do you offer smaller accent-wall projects in St. Pete?",
        a: "Absolutely. Not every project has to be a full media wall — accent walls in dining rooms, offices and bedrooms are a real part of what we do.",
      },
      {
        q: "What areas around St. Pete do you cover?",
        a: "Gulfport, Tierra Verde, Pinellas Park and Kenneth City are all part of our regular service zone.",
      },
    ],
  },

  sarasota: {
    slug: "sarasota",
    name: "Sarasota",
    state: "FL",
    primary_keyword: "custom media walls Sarasota",
    supporting: [
      "luxury TV walls Sarasota",
      "fireplace media walls Sarasota",
      "accent wall installation Sarasota",
    ],
    seo_title: "Custom Media Walls Sarasota | Swift Luxury Walls",
    meta_description:
      "Luxury TV walls, fireplace media walls and accent installations for Sarasota homes — from Lakewood Ranch to Longboat Key.",
    hero_heading: "Luxury TV Walls & Custom Media Walls in Sarasota, FL",
    hero_sub:
      "Sarasota homeowners tend to know what they want — and how it should feel to walk into a room. We build media walls, feature installs and fireplace walls that meet that standard.",
    neighborhoods: [
      "Downtown Sarasota",
      "Lakewood Ranch",
      "Longboat Key",
      "Siesta Key",
      "The Meadows",
      "Palmer Ranch",
      "St. Armands",
    ],
    focus:
      "Sarasota's design bar is high — architects and interior designers set a tone across the market, and we build to that tone. Marble slabs, backlit niches, seamless millwork and integrated lighting are common asks here.",
    services_intro:
      "Luxury custom media walls, fireplace TV walls, marble and stone-veneer feature walls, integrated lighting and custom built-ins for Sarasota properties.",
    local_details:
      "Sarasota is a longer trip from our shop, so we plan Sarasota-area builds in efficient blocks — grouped consults, batched installs, and clear communication so the schedule stays predictable for you. Coordination with your architect or designer is welcome and encouraged.",
    faqs: [
      {
        q: "Do you work with interior designers and architects in Sarasota?",
        a: "Yes. We're happy to work from designer drawings, quote against a spec, and communicate through your primary designer on the project.",
      },
      {
        q: "Can you install natural stone or slab marble?",
        a: "Yes — we work with slab suppliers and can fit natural marble, quartzite or porcelain slabs. Lead times are longer than for panel systems, and cost reflects the material.",
      },
      {
        q: "Do you service Lakewood Ranch, Longboat Key and Siesta Key?",
        a: "Yes — all three are regular parts of our Sarasota-area service.",
      },
      {
        q: "How is a Sarasota project priced versus Tampa?",
        a: "Design and craftsmanship pricing is the same. Longer travel days for very large multi-day installs may be reflected on the quote, and we'll always be transparent about it up front.",
      },
    ],
  },

  bradenton: {
    slug: "bradenton",
    name: "Bradenton",
    state: "FL",
    primary_keyword: "custom media walls Bradenton",
    supporting: [
      "entertainment walls Bradenton",
      "wood slat wall installation Bradenton",
      "fireplace TV walls Bradenton",
    ],
    seo_title: "Custom Media Walls Bradenton | Swift Luxury Walls",
    meta_description:
      "Custom entertainment walls, wood slat installations and fireplace TV walls for Bradenton homes and West Bradenton communities.",
    hero_heading: "Custom Media Walls & Wood Slat Wall Installation in Bradenton, FL",
    hero_sub:
      "Bradenton has a great mix of established neighborhoods and newer builds — perfect ground for feature walls that feel intentional and finished, not stuck on.",
    neighborhoods: ["West Bradenton", "Palma Sola", "Lakewood Ranch (shared)", "Anna Maria Island", "Cortez", "Bayshore Gardens"],
    focus:
      "Bradenton and Manatee County builds run the full range — coastal condos, single-family homes and new-construction great rooms. We match the wall build to the room, not the other way around.",
    services_intro:
      "Custom entertainment walls, wood-slat feature walls, fireplace TV walls, built-in cabinetry and floating consoles for Bradenton, Palma Sola and Anna Maria Island homes.",
    local_details:
      "Coastal Bradenton and Anna Maria projects share the same coastal-humidity considerations as Clearwater — we choose finishes and hardware accordingly. We coordinate with condo associations on the island and pick install windows around bridge access and traffic.",
    faqs: [
      {
        q: "Do you build on Anna Maria Island?",
        a: "Yes — we schedule with bridge access and association windows in mind so install days go smoothly.",
      },
      {
        q: "Can you do a slat wall around an existing electric fireplace?",
        a: "Yes — we plan the slat run around the fireplace with proper clearances and can hide the seams so it reads as a single piece.",
      },
      {
        q: "What is the smallest project you take on?",
        a: "Accent walls of any size, provided we can schedule them alongside other work in the area for efficiency. It's worth a call — we're pretty flexible in Bradenton.",
      },
      {
        q: "How is Lakewood Ranch handled — Bradenton or Sarasota?",
        a: "Lakewood Ranch straddles both. Either page works — we service the whole community.",
      },
    ],
  },
};

// Simple ordered list for the hub
export const CITY_LIST = [
  "tampa",
  "brandon",
  "riverview",
  "land-o-lakes",
  "clearwater",
  "st-petersburg",
  "sarasota",
  "bradenton",
];
